if (!customElements.get('collection-tabs-bar')) {
  customElements.define(
    'collection-tabs-bar',
    class CollectionTabsBar extends HTMLElement {
      constructor() {
        super();

        this.tabs = Array.from(this.querySelectorAll('[data-collection-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-collection-tab-panel]'));
        this.abortController = null;

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.onTabClick.bind(this));
        });

        // Support browser back/forward between tabbed collections.
        window.addEventListener('popstate', this.onPopState.bind(this));
      }

      onTabClick(event) {
        const tab = event.currentTarget;
        if (tab.classList.contains('is-active') || this.classList.contains('is-loading')) return;

        this.setActiveTab(tab.dataset.handle);
        this.switchToUrl(tab.dataset.url, tab.dataset.handle, true);
      }

      onPopState() {
        const handle = this.getHandleFromLocation();
        if (!handle || handle === this.dataset.currentHandle) return;

        const tab = this.tabs.find((item) => item.dataset.handle === handle);
        if (tab) {
          this.setActiveTab(tab.dataset.handle);
          this.switchToUrl(tab.dataset.url, tab.dataset.handle, false);
        }
      }

      getHandleFromLocation() {
        const match = window.location.pathname.match(/\/collections\/([^/]+)/);
        return match ? match[1] : null;
      }

      switchToUrl(url, handle, updateHistory) {
        if (!url) return;

        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        this.classList.add('is-loading');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

            this.renderById(parsedHTML, 'FacetFiltersContainer');
            this.renderById(parsedHTML, 'MobileFacetFiltersContainer');
            this.renderById(parsedHTML, 'FacetFiltersActive');
            this.renderById(parsedHTML, 'ProductGridContainer');
            this.renderById(parsedHTML, 'ProductCount');
            this.renderById(parsedHTML, 'SortByContainer');
            this.renderById(parsedHTML, 'SortByContainerMobile');

            this.setActiveTab(handle);

            const newTitle = parsedHTML.querySelector('title');
            if (newTitle) document.title = newTitle.textContent;

            if (updateHistory) {
              history.pushState({ collectionTab: handle }, '', url);
            }

            this.dataset.currentHandle = handle;

            const layoutSwitcher = document.querySelector('#ProductGridWrapper layout-switcher');
            if (layoutSwitcher && layoutSwitcher.querySelector('.btn--active')) {
              layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.btn--active'));
            }

            document.dispatchEvent(new CustomEvent('collection:rerendered'));
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            // Fall back to a normal navigation if the fetch fails for any reason.
            window.location.href = url;
          })
          .finally(() => {
            this.classList.remove('is-loading');
          });
      }

      renderById(parsedHTML, id) {
        const target = document.getElementById(id);
        const source = parsedHTML.getElementById(id);
        if (!target || !source) return;

        // IMPORTANT: do NOT use `target.innerHTML = source.innerHTML` here.
        // Re-parsing HTML text constructs this theme's customized built-in
        // elements (select[is="facet-short"], form[is="facet-form"],
        // details[is="accordion-details"], price-range) BEFORE their children
        // exist. Their constructors read child nodes, throw, and the elements
        // are left without their JS behaviour. On iOS/Safari (which needs the
        // `is` polyfill) one failed constructor aborts the whole upgrade batch,
        // which is why the mobile "Sort by" select stopped working after a tab
        // switch while desktop kept working. Importing the already-parsed DOM
        // nodes instead means custom elements are only upgraded once they are
        // connected with their full subtree, so everything initialises safely.
        const fragment = document.createDocumentFragment();
        Array.from(source.childNodes).forEach((node) => {
          fragment.appendChild(document.importNode(node, true));
        });

        if (typeof target.replaceChildren === 'function') {
          target.replaceChildren(fragment);
        } else {
          target.innerHTML = '';
          target.appendChild(fragment);
        }
      }

      setActiveTab(handle) {
        this.tabs.forEach((tab) => {
          const isActive = tab.dataset.handle === handle;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive);
        });

        this.panels.forEach((panel) => {
          panel.classList.toggle('is-active', panel.dataset.handle === handle);
        });
      }
    }
  );
}